import { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { useMapboxKey } from "@/components/maps/useMapboxKey";
import { fitBounds } from "@/utils/map";
import { USA_BOUNDS, USA_CENTER, MARKETS_STYLE_URL, DEFAULT_PADDING, DEFAULT_MAP_CONFIG } from "../constants";

/**
 * Hook for initializing and managing a Mapbox map instance
 * @param {Object} options - Configuration options
 * @param {Array} options.initialBounds - Initial map bounds [[west, north], [east, south]]
 * @param {Object} options.padding - Padding for fitBounds
 * @param {string} options.style - Mapbox style URL
 * @param {boolean} options.disableScroll - Disable scroll zoom
 * @param {boolean} options.disableDoubleClickZoom - Disable double click zoom
 * @param {Function} options.onError - Error callback
 * @returns {Object} Map state and refs
 */
export const useMarketMap = ({
  initialBounds = USA_BOUNDS,
  padding = DEFAULT_PADDING,
  style = MARKETS_STYLE_URL,
  disableScroll = true,
  disableDoubleClickZoom = true,
  onError,
} = {}) => {
  const mapRef = useRef(null);
  const mapContainer = useRef(null);
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [mapInitialised, setMapInitialised] = useState(false);
  const [error, setError] = useState(false);

  const mapboxAccessKey = useMapboxKey(true);

  const handleError = useCallback(
    (e) => {
      setError(true);
      onError?.(e);
    },
    [onError],
  );

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !mapboxAccessKey) return;

    mapboxgl.accessToken = mapboxAccessKey;
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style,
      center: USA_CENTER,
      bounds: initialBounds,
      fitBoundsOptions: { padding, duration: 400 },
      ...DEFAULT_MAP_CONFIG,
    });
    mapRef.current = map;

    map.on("load", () => {
      setMapIsLoaded(true);
      // Set min zoom to current level to prevent zooming out beyond default view
      map.setMinZoom(map.getZoom());
    });

    map.on("error", handleError);

    if (disableScroll) {
      map.scrollZoom.disable();
    }
    if (disableDoubleClickZoom) {
      map.doubleClickZoom.disable();
    }

    const handleResize = () => {
      fitBounds(map, padding, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      map.remove();
      window.removeEventListener("resize", handleResize);
    };
  }, [mapboxAccessKey, style, disableScroll, disableDoubleClickZoom]);

  // Mark as initialised when map is ready
  const markInitialised = useCallback(() => {
    setMapInitialised(true);
  }, []);

  return {
    mapRef,
    mapContainer,
    mapIsLoaded,
    mapInitialised,
    markInitialised,
    error,
    padding,
  };
};
